import { useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useTheme } from "next-themes";
import {
  LayoutDashboard, ClipboardList, Cpu, Bot, Megaphone, X, TrendingUp, UserRound, Building2, Car, Users, FileText, FileSignature, Settings, LogOut, Moon, Sun, Menu,
} from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { group: "Overview", items: [{ to: "/", label: "Dashboard", icon: LayoutDashboard, end: true }, { to: "/driver", label: "My Dashboard", icon: LayoutDashboard, driver: true, only: true }] },
  { group: "Operations", items: [{ to: "/duties", label: "Duties", icon: ClipboardList, staff: true }] },
  {
    group: "Masters",
    items: [
      { to: "/companies", label: "Companies", icon: Building2 },
      { to: "/vehicles", label: "Own Vehicles", icon: Car, staff: true },
      { to: "/owners", label: "Market Owners", icon: Users, staff: true },
      { to: "/drivers", label: "Drivers & Payments", icon: UserRound },
    ],
  },
  {
    group: "Documents",
    items: [
      { to: "/invoices", label: "Tax Invoices", icon: FileText },
      { to: "/quotations", label: "Quotations", icon: FileSignature },
    ],
  },
  {
    group: "AI Business Development Center",
    items: [
      { to: "/ai", label: "BD Center Overview", icon: Cpu, end: true },
      { to: "/ai/marketing", label: "AI Sales Hunter", icon: Bot },
      { to: "/ai/social", label: "AI Marketing Studio", icon: Megaphone },
      { to: "/ai/advisor", label: "AI Growth Advisor", icon: TrendingUp },
    ],
  },
  { group: "System", items: [{ to: "/settings", label: "Settings", icon: Settings }] },
];

function Brand({ onClose }) {
  return (
    <div className="flex items-center gap-2 px-3 h-20 border-b border-white/10 shrink-0">
      <div className="flex-1 min-w-0 h-14 rounded-lg bg-white px-2 flex items-center justify-center" data-testid="sidebar-logo">
        <img src="/ekdant-logo-transparent.png" alt="Ekdant Tours & Travels" className="max-h-12 w-auto max-w-full object-contain" />
      </div>
      {onClose && (
        <button onClick={onClose} aria-label="Close menu" data-testid="mobile-menu-close-btn"
          className="shrink-0 h-10 w-10 rounded-lg grid place-items-center bg-white/10 text-white hover:bg-amber-500 hover:text-slate-950 transition-colors">
          <X className="h-5 w-5" />
        </button>
      )}
    </div>
  );
}

function SideNav({ onNavigate, onClose }) {
  const { logout, user } = useAuth();
  return (
    <div className="flex flex-col h-full bg-slate-950 text-slate-300 grain">
      <Brand onClose={onClose} />
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
        {NAV.map((g) => ({ ...g, items: g.items.filter((it) => (user?.role === "admin" ? !it.only : user?.role === "driver" ? it.driver : it.staff)) })).filter((g) => g.items.length).map((g) => (
          <div key={g.group}>
            <div className="px-3 mb-1.5 text-[10px] font-bold tracking-[0.16em] uppercase text-slate-500">{g.group}</div>
            {g.items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                onClick={onNavigate}
                data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 h-11 lg:h-10 rounded-md text-sm font-medium transition-colors ${
                    isActive ? "bg-white/10 text-white border-l-2 border-amber-500" : "hover:bg-white/5 hover:text-white"
                  }`
                }
              >
                <it.icon className="h-4 w-4" />
                {it.label}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>
      <div className="p-3 border-t border-white/10 shrink-0 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        <div className="px-3 py-2 text-xs">
          <div className="text-white font-semibold">{user?.name}</div>
          <div className="text-slate-500 truncate">{user?.role === "driver" ? user?.mobile || user?.driver_code : user?.email}</div>
        </div>
        <button
          onClick={logout}
          data-testid="logout-btn"
          className="w-full flex items-center gap-3 px-3 h-10 rounded-md text-sm hover:bg-rose-500/10 hover:text-rose-400 transition-colors"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>
    </div>
  );
}

export default function AppLayout() {
  const [open, setOpen] = useState(false);
  const { theme, setTheme } = useTheme();
  const loc = useLocation();
  return (
    <div className="min-h-screen flex overflow-x-clip">
      <aside className="hidden lg:block w-64 shrink-0 fixed inset-y-0 left-0 z-30">
        <SideNav />
      </aside>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" hideClose className="p-0 w-[85vw] max-w-[300px] border-0" data-testid="mobile-drawer">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <SideNav onNavigate={() => setOpen(false)} onClose={() => setOpen(false)} />
        </SheetContent>
      </Sheet>
      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="sticky top-0 z-20 h-14 lg:h-16 flex items-center gap-2 sm:gap-3 px-3 sm:px-6 lg:px-8 bg-background/80 backdrop-blur-xl border-b">
          <Button variant="ghost" size="icon" className="lg:hidden h-10 w-10 shrink-0" aria-label="Open menu" onClick={() => setOpen(true)} data-testid="mobile-menu-btn">
            <Menu className="h-5 w-5" />
          </Button>
          <div className="lg:hidden flex items-center min-w-0 h-10 rounded-md bg-white px-2" data-testid="header-logo">
            <img src="/ekdant-logo-transparent.png" alt="Ekdant Tours & Travels" className="h-8 w-auto object-contain" />
          </div>
          <div className="text-xs text-muted-foreground hidden lg:block">
            {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              data-testid="theme-toggle-btn"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            >
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
          </div>
        </header>
        <main key={loc.pathname} className="px-3 py-4 sm:p-6 lg:p-8 rise max-w-[1600px] min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
